import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { Page, Toolbar } from "@/client/components/page";
import { HeldCount, PagePending } from "@/client/components/page-pending";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { ensureTenantData } from "@/client/lib/session";
import { openCount } from "@/client/lib/task-days";
import { tasksQuery } from "@/client/lib/tasks-query";
import { useNow } from "@/client/lib/use-now";
import { dayIn } from "@/features/attendance/rules";
import { peopleQuery } from "@/features/people/people-query";
import { profileQuery } from "@/features/settings/settings-query";
import { GivenTasks } from "@/features/tasks/components/given-tasks";
import {
  TasksHeading,
  TasksScreen,
  TasksTabs,
} from "@/features/tasks/components/tasks-screen";
import { tasksCopy } from "@/features/tasks/copy";
import { givenTasks } from "@/features/tasks/given";
import { TASKS_VIEW_DEFAULTS, tasksViewSearch } from "@/features/tasks/schemas";

export const Route = createFileRoute("/_authenticated/$slug/tasks")({
  validateSearch: tasksViewSearch,
  search: { middlewares: [stripSearchParams(TASKS_VIEW_DEFAULTS)] },
  pendingMs: PENDING_MS,
  head: ({ match }) => ({
    meta: [
      {
        title: pageTitle(tasksCopy.tasks.pageTitle, match.context.companyName),
      },
    ],
  }),
  loader: async ({ context }) => {
    await Promise.all([
      ensureTenantData(context.queryClient, tasksQuery),
      ensureTenantData(context.queryClient, profileQuery),
    ]);
  },
  pendingComponent: TasksPending,
  component: Tasks,
});

/** The tabs drawn for real while the list loads, so it arrives under them without a jump. */
function TasksPending() {
  const view = Route.useSearch({ select: (search) => search.view });
  return (
    <PagePending
      shape="list"
      dated
      header={
        <>
          <TasksHeading />
          <Toolbar
            views={
              <TasksTabs
                view={view}
                mine={<HeldCount />}
                given={<HeldCount />}
                onView={() => {}}
              />
            }
          />
        </>
      }
    />
  );
}

function Tasks() {
  const { companyTimezone } = Route.useRouteContext();
  const view = Route.useSearch({ select: (search) => search.view });
  const navigate = Route.useNavigate();
  const { data: profile } = useSuspenseQuery(profileQuery);
  const { data: people = [] } = useQuery(peopleQuery);
  const { data: tasks } = useSuspenseQuery(tasksQuery);
  // Every minute, so the groups turn over at midnight on the company's clock.
  const now = useNow(60_000);
  const today = dayIn(companyTimezone, now);
  const me = profile.personId;
  const given = givenTasks(tasks, me, now);

  return (
    <Page shape="list" dated>
      <TasksHeading />
      <Toolbar
        views={
          <TasksTabs
            view={view}
            mine={openCount(tasks, me)}
            given={given.inProgress.length + given.todo.length}
            onView={(next) =>
              void navigate({
                to: ".",
                search: (prev: object) => ({ ...prev, view: next }),
              })
            }
          />
        }
      />
      {view === "given" ? (
        <GivenTasks me={me} people={people} today={today} now={now} />
      ) : (
        <TasksScreen me={me} people={people} today={today} />
      )}
    </Page>
  );
}
